import { Box, Button, Skeleton, Typography } from "@mui/material";
import { AppModal, bodyTextSx } from "global/components/AppModal";
import myPlanTokens from "screens/Settings/MyPlan/MyPlan.tokens";
import {
  accentButtonSx,
  brandPlateSx,
  columnHeaderSx,
  emptyStateSx,
  listRowSx,
  paymentCardCellSx,
  paymentCardTextSx,
  paymentRowActionSx,
  pillSx,
  sectionHeaderActionSx,
  planCardSx,
  rowTextSx,
  sectionDescSx,
  sectionHeaderSx,
  sectionTitleSx,
} from "screens/Settings/MyPlan/MyPlan.styles";
import {
  getCardLogo,
  getCardPlate,
  PaymentCardRow,
} from "screens/Settings/MyPlan/components/MyPlanHelper";
import { TrashIcon } from "screens/Settings/MyPlan/components/PlanIcons";
import EmptyState from "global/components/EmptyState/EmptyState";

const t = myPlanTokens;

interface CustomProps {
  isLoading: boolean;
  selected: string;
  setSelected: Function;
  paymentMethods: PaymentCardRow[];
  setDefaultCardHandler: any;
  cardAsPrimaryModal: any;
  setCardAsPrimaryModal: (value: boolean) => void;
  openDeleteModal: any;
  setOpenDeleteModal: (value: boolean) => void;
  deleteCardHandler: any;
  cardId: any;
  setCardId: (value: string) => void;
  setPreviousSelected: any;
  previousSelected: any;
  canAddPayment?: boolean;
  addPaymentDisabled?: boolean;
  onAddPaymentMethod?: () => void;
}

const PaymentMethods = (props: CustomProps) => {
  const {
    isLoading,
    selected,
    setSelected,
    paymentMethods,
    setDefaultCardHandler,
    setCardAsPrimaryModal,
    cardAsPrimaryModal,
    openDeleteModal,
    setOpenDeleteModal,
    deleteCardHandler,
    cardId,
    setCardId,
    setPreviousSelected,
    previousSelected,
    canAddPayment,
    addPaymentDisabled,
    onAddPaymentMethod,
  } = props;

  const handleSelectPrimaryCard = (selectedId: string) => {
    setPreviousSelected(selected);
    setSelected(selectedId);
    setCardAsPrimaryModal(true);
  };

  const handleCancel = () => {
    if (cardAsPrimaryModal) {
      setSelected(previousSelected);
      setCardAsPrimaryModal(false);
    } else {
      setOpenDeleteModal(false);
    }
  };

  /** 16px ring; the primary card carries an 8px accent dot. */
  const getRadio = (card: PaymentCardRow) => {
    const isSelected = selected === card.id;
    return (
      <Box
        role="radio"
        aria-checked={isSelected}
        aria-label={`Set card ending ${card.number} as primary`}
        aria-disabled={card.isExpired}
        tabIndex={card.isExpired ? -1 : 0}
        onClick={() => {
          if (card.isExpired || isSelected) return;
          handleSelectPrimaryCard(card.id);
        }}
        onKeyDown={(event) => {
          if (card.isExpired || isSelected) return;
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            handleSelectPrimaryCard(card.id);
          }
        }}
        sx={{
          width: t.size.radio,
          height: t.size.radio,
          // Design draws the selected ring outside the 16px box and the empty
          // rings inside it, so the primary card reads slightly larger.
          boxSizing: isSelected ? "content-box" : "border-box",
          borderRadius: t.radius.pill,
          border: `${t.border.control} solid ${
            isSelected ? t.color.accent : t.color.radioBorder
          }`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: card.isExpired ? "not-allowed" : "pointer",
          opacity: card.isExpired ? 0.6 : 1,
        }}
      >
        {isSelected && (
          <Box
            sx={{
              width: t.size.radioDot,
              height: t.size.radioDot,
              borderRadius: t.radius.pill,
              bgcolor: t.color.ctaBg,
            }}
          />
        )}
      </Box>
    );
  };

  /** Network logo, falling back to a text plate for unmapped brands. */
  const getBrandMark = (card: PaymentCardRow) => {
    const logo = getCardLogo(card.brand);
    if (!logo) {
      const plate = getCardPlate(card.brand);
      return (
        <Box component="span" sx={brandPlateSx(plate.color)}>
          {plate.label}
        </Box>
      );
    }
    return (
      <Box
        component="img"
        src={logo}
        alt={`${card.brand} credit card logo`}
        sx={{
          width: t.size.cardLogoWidth,
          flexShrink: 0,
          display: "block",
        }}
      />
    );
  };

  const getRow = (card: PaymentCardRow, index: number) => {
    return (
      <Box
        key={card.id}
        sx={listRowSx(t.grid.paymentRow, index === paymentMethods.length - 1)}
      >
        {getRadio(card)}
        <Box sx={paymentCardCellSx}>
          {getBrandMark(card)}
          {/* Stacked below `md`: the badge has no room beside a masked number. */}
          <Box sx={paymentCardTextSx}>
            <Box
              component="span"
              sx={rowTextSx(
                card.isExpired ? t.color.muted : t.color.ink,
                t.weight.medium,
              )}
            >
              {`XXXX-XXXX-XXXX-${card.number}`}
            </Box>
            {card.isExpired && (
              <Box
                component="span"
                sx={pillSx({
                  color: t.color.dangerText,
                  bgcolor: t.color.dangerBg,
                  fontSize: t.font.chip,
                })}
              >
                EXPIRED
              </Box>
            )}
          </Box>
        </Box>
        <Box sx={paymentRowActionSx}>
          <Box
            component="button"
            type="button"
            aria-label={`Delete card ending ${card.number}`}
            onClick={() => {
              setOpenDeleteModal(true);
              setCardId(card.id);
            }}
            sx={{
              p: 0,
              border: "none",
              bgcolor: "transparent",
              display: "flex",
              cursor: "pointer",
              color: t.color.muted,
              "&:hover": { color: t.color.ink },
            }}
          >
            <TrashIcon color="currentColor" />
          </Box>
        </Box>
      </Box>
    );
  };

  const getLoadingRows = () =>
    [...Array(4)].map((_, index) => (
      <Box key={index} sx={listRowSx(t.grid.paymentRow, index === 3)}>
        <Skeleton variant="circular" width={16} height={16} />
        <Skeleton variant="text" width="45%" height={20} />
        <Box />
      </Box>
    ));

  const getEmptyRow = () => (
    <EmptyState
      icon="invoice"
      accent="amber"
      title="No payment methods yet"
      body="Add a card and it is used for your subscription and any overage charges."
    />
  );

  return (
    <>
      <Box sx={planCardSx}>
        <Box sx={sectionHeaderSx(false)}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "2px",
              minWidth: 0,
            }}
          >
            <Box component="h2" sx={sectionTitleSx}>
              Payment Methods
            </Box>
            <Box sx={sectionDescSx}>
              Your primary card is charged each billing cycle.
            </Box>
          </Box>
          {canAddPayment && (
            <Box sx={sectionHeaderActionSx}>
              <Button
                disableElevation
                sx={accentButtonSx(t.size.smallButtonHeight, "18px")}
                disabled={addPaymentDisabled}
                onClick={onAddPaymentMethod}
              >
                Add Payment Method
              </Button>
            </Box>
          )}
        </Box>

        <Box sx={columnHeaderSx(t.grid.paymentRow)}>
          <Box component="span">PRIMARY</Box>
          <Box component="span">CARD</Box>
          <Box component="span" />
        </Box>

        {isLoading ? (
          getLoadingRows()
        ) : paymentMethods.length > 0 ? (
          <Box role="radiogroup" aria-label="Set primary payment card">
            {paymentMethods.map(getRow)}
          </Box>
        ) : (
          getEmptyRow()
        )}
      </Box>

      {openDeleteModal && (
        <AppModal
          open={openDeleteModal}
          onClose={() => handleCancel()}
          title="Please confirm"
          tone="destructive"
          size="sm"
          primaryLabel="Delete"
          onPrimary={() => deleteCardHandler(cardId)}
          cancelLabel="Cancel"
          onCancel={() => handleCancel()}
        >
          <Typography sx={bodyTextSx}>
            Do you really want to delete this card?
          </Typography>
        </AppModal>
      )}
      {cardAsPrimaryModal && (
        <AppModal
          open={cardAsPrimaryModal}
          onClose={() => handleCancel()}
          title="Set as primary card"
          tone="neutral"
          size="sm"
          primaryLabel="Yes"
          onPrimary={() => setDefaultCardHandler()}
          cancelLabel="Cancel"
          onCancel={() => handleCancel()}
        >
          <Typography sx={bodyTextSx}>
            Are you sure you want to set your card primary ?
          </Typography>
        </AppModal>
      )}
    </>
  );
};

export default PaymentMethods;
